import ResponsiveTable from "../../components/tables/ResponsiveTable.jsx";
import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/ihthisabi/AuthContext'
import { api } from '../../utils/ihthisabi/api'
import UserManagement from '../../components/ihthisabi/UserManagement'
import UserManagementDynamic from '../../components/ihthisabi/UserManagementDynamic'
import AbroadCountryManagement from '../../components/ihthisabi/AbroadCountryManagement'
import AbroadMemberManagement from '../../components/ihthisabi/AbroadMemberManagement'
import AbroadSubmissions from './AbroadSubmissions'
import UnitAdminProfileModal from '../../components/ihthisabi/UnitAdminProfileModal'
import DistrictAdminProfileModal from '../../components/ihthisabi/DistrictAdminProfileModal'
import ConfirmationModal from '../../components/ihthisabi/ConfirmationModal'
import Pagination from '../../components/ihthisabi/Pagination'
import { 
  Users, 
  FileText, 
  TrendingUp,
  Calendar,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  Activity,
  Eye,
  XCircle,
  Settings,
  Phone,
  Mail,
  MessageSquare,
  Send,
  Globe,
  Archive,
  Database,
  ChevronDown,
  ChevronRight,
  Search,
  Plus,
  MapPin,
  SlidersHorizontal
} from 'lucide-react'
import toast from 'react-hot-toast'
import UnitReplyPanel from '../../components/ihthisabi/UnitReplyPanel'
import ArchiveManagement from '../../components/ihthisabi/ArchiveManagement'
import MasterDataManagement from '../../components/ihthisabi/MasterDataManagement'
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, RadialBarChart, RadialBar, PolarAngleAxis, CartesianGrid } from 'recharts'

// Shared chart styling: no heavy axis rules, muted ticks, soft floating tooltip.
const AXIS = { tick: { fontSize: 10, fill: '#98A2B3' }, axisLine: false, tickLine: false }
const GRID = { stroke: 'rgba(16,24,40,0.06)', vertical: false }
const TOOLTIP = {
  contentStyle: {
    borderRadius: 12,
    border: 'none',
    boxShadow: '0 4px 8px rgba(16,24,40,.04), 0 12px 32px rgba(16,24,40,.10)',
    fontSize: 11,
  },
  cursor: { fill: 'rgba(16,24,40,0.04)' },
}
// Share of `total` as a percentage, floored to one decimal (same rule as the
// backend) so it only reads 100% when every member submitted.
const pctOf = (n, total) => (total > 0 ? Math.floor((n / total) * 1000) / 10 : 0)
// Dynamic Forms temporarily hidden

const AdminDashboard = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isAuthenticated, loading: authLoading } = useAuth()
  const [dashboardData, setDashboardData] = useState(null)
  const [submissions, setSubmissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  const getTabFromPath = (pathname) => {
    if (pathname.endsWith('/members')) return 'users'
    if (pathname.endsWith('/unit-admins')) return 'unitadmins'
    if (pathname.endsWith('/district-admins')) return 'districtadmins'
    if (pathname.endsWith('/unit-reply')) return 'unitreply'
    if (pathname.endsWith('/user-management')) return 'user-management'
    if (pathname.endsWith('/archive')) return 'archive'
    if (pathname.endsWith('/abroad-countries')) return 'abroad-countries'
    if (pathname.endsWith('/abroad-members')) return 'abroad-members'
    if (pathname.endsWith('/master-data')) return 'master-data'
    return 'dashboard'
  }

  const activeTab = getTabFromPath(location.pathname)

  const [unitAdmins, setUnitAdmins] = useState([])
  const [unitAdminLoading, setUnitAdminLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedDistrict, setSelectedDistrict] = useState('')
  const [districts, setDistricts] = useState([])
  const [unitAdminMessage, setUnitAdminMessage] = useState({ title: '', description: '' })
  const [unitAdminMessageSending, setUnitAdminMessageSending] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadResult, setUploadResult] = useState(null)
  const [pagination, setPagination] = useState({ current: 1, pages: 1, total: 0 })
  const [selectedUnitAdminId, setSelectedUnitAdminId] = useState(null)
  const [showProfileModal, setShowProfileModal] = useState(false)

  // District Admins tab state
  const [districtAdmins, setDistrictAdmins] = useState([])
  const [districtAdminLoading, setDistrictAdminLoading] = useState(false)
  const [districtAdminSearchTerm, setDistrictAdminSearchTerm] = useState('')
  const [selectedDistrictAdminDistrict, setSelectedDistrictAdminDistrict] = useState('')
  const [districtAdminDistricts, setDistrictAdminDistricts] = useState([])
  const [districtAdminPagination, setDistrictAdminPagination] = useState({ current: 1, pages: 1, total: 0 })
  const [selectedDistrictAdminId, setSelectedDistrictAdminId] = useState(null)
  const [showDistrictAdminModal, setShowDistrictAdminModal] = useState(false)
  const [districtAdminFiltersOpen, setDistrictAdminFiltersOpen] = useState(false)
  const [unitAdminFiltersOpen, setUnitAdminFiltersOpen] = useState(false)
  const [uploadCardOpen, setUploadCardOpen] = useState(false)
  const [broadcastCardOpen, setBroadcastCardOpen] = useState(false)

  // District-wise submission stats + chart district selection (Dashboard tab)
  const [districtStats, setDistrictStats] = useState([])
  const [districtStatsLoading, setDistrictStatsLoading] = useState(false)
  const [selectedChartDistricts, setSelectedChartDistricts] = useState([])
  const [chartDistrictsInitialized, setChartDistrictsInitialized] = useState(false)
  const [chartDistrictDropdownOpen, setChartDistrictDropdownOpen] = useState(false)
  const [chartDistrictSearch, setChartDistrictSearch] = useState('')
  const chartDistrictDropdownRef = useRef(null)

  // Submitted / Pending units (Dashboard tab)
  const [unitsStatus, setUnitsStatus] = useState(null)
  const [unitsStatusLoading, setUnitsStatusLoading] = useState(false)
  const [unitsStatusDistrict, setUnitsStatusDistrict] = useState('')
  const [unitsStatusArea, setUnitsStatusArea] = useState('')
  const [unitsListTab, setUnitsListTab] = useState('notSubmitted')

  const isSuperAdmin = user?.isAdmin || user?.role === 'mainAdmin'

  useEffect(() => {
    // Only fetch data if user is authenticated
    console.log('AdminDashboard useEffect - User:', user)
    console.log('User role:', user?.role)
    console.log('Is admin?', user?.role === 'admin' || user?.role === 'mainAdmin')
    
    if (user && (user.role === 'admin' || user.role === 'mainAdmin')) {
      fetchDashboardData()
      fetchSubmissions()
      fetchDistrictStats()
      fetchUnitsStatus()
    }
  }, [user])

  // Default to just the single largest district — the full list is too dense
  // to be useful as a default view. Districts are pre-sorted by member count desc.
  useEffect(() => {
    if (!chartDistrictsInitialized && districtStats.length > 0) {
      setSelectedChartDistricts([districtStats[0].district])
      setChartDistrictsInitialized(true)
    }
  }, [districtStats, chartDistrictsInitialized])

  // Close the district-picker dropdown when clicking outside of it
  useEffect(() => {
    if (!chartDistrictDropdownOpen) return
    const handleClickOutside = (e) => {
      if (chartDistrictDropdownRef.current && !chartDistrictDropdownRef.current.contains(e.target)) {
        setChartDistrictDropdownOpen(false)
      }
    }
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setChartDistrictDropdownOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [chartDistrictDropdownOpen])

  const toggleChartDistrict = (district) => {
    setSelectedChartDistricts((prev) =>
      prev.includes(district) ? prev.filter((d) => d !== district) : [...prev, district]
    )
  }

  // Lazy-load unit admins data when that tab is active
  useEffect(() => {
    if (activeTab === 'unitadmins' && user && (user.role === 'admin' || user.role === 'mainAdmin')) {
      if (unitAdmins.length === 0) fetchUnitAdmins()
      if (districts.length === 0) fetchDistricts()
    }
  }, [activeTab, user])

  // Lazy-load district admins data when that tab is active
  useEffect(() => {
    if (activeTab === 'districtadmins' && user && (user.role === 'admin' || user.role === 'mainAdmin')) {
      if (districtAdmins.length === 0) fetchDistrictAdmins()
      if (districtAdminDistricts.length === 0) fetchDistrictAdminDistricts()
    }
  }, [activeTab, user])

  // Add retry functionality for failed requests
  const retryFailedRequests = () => {
    if (user && user.role === 'admin') {
      setLoading(true)
      setHasError(false)
      fetchDashboardData()
      fetchSubmissions()
    }
  }

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/ihthisabi/admin/dashboard/stats')
      setDashboardData(response.data.data)
      console.log(response.data.data)
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
      setHasError(true)
      
      // Don't show error toast for network issues or server errors
      // Only show for actual authentication issues
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.')
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again later.')
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection.')
      } else {
        toast.error('Failed to load dashboard data')
      }
    }
  }

  const fetchSubmissions = async () => {
    try {
      const response = await api.get('/ihthisabi/admin/submissions')
      setSubmissions(response.data.data.submissions || [])
    } catch (error) {
      console.error('Failed to fetch submissions:', error)
      setHasError(true)
      
      // Don't show error toast for network issues or server errors
      // Only show for actual authentication issues
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.')
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again later.')
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection.')
      } else {
        toast.error('Failed to load submissions')
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchUnitAdmins = async (search = '', district = '', page = 1) => {
    try {
      setUnitAdminLoading(true)
      const params = {}
      if (search) params.search = search
      if (district) params.district = district
      params.page = page
      params.limit = 10

      const response = await api.get('/ihthisabi/admin/unitadmins', { params })
      console.log('Unit admins response:', response.data.data)
      setUnitAdmins(response.data.data.unitAdmins || [])
      setPagination(response.data.data.pagination || { current: 1, pages: 1, total: 0 })
    } catch (error) {
      console.error('Error fetching unit admins:', error)
      toast.error('Failed to fetch unit admins')
    } finally {
      setUnitAdminLoading(false)
    }
  }

  const fetchDistrictAdmins = async (search = '', district = '', page = 1) => {
    try {
      setDistrictAdminLoading(true)
      const params = {}
      if (search) params.search = search
      if (district) params.district = district
      params.page = page
      params.limit = 10

      const response = await api.get('/ihthisabi/admin/district-admins', { params })
      setDistrictAdmins(response.data.data.districtAdmins || [])
      setDistrictAdminPagination(response.data.data.pagination || { current: 1, pages: 1, total: 0 })
    } catch (error) {
      console.error('Error fetching district admins:', error)
      toast.error('Failed to fetch district admins')
    } finally {
      setDistrictAdminLoading(false)
    }
  }

  const fetchDistrictAdminDistricts = async () => {
    try {
      const response = await api.get('/ihthisabi/admin/district-admins/districts')
      setDistrictAdminDistricts(response.data.data.districts || [])
    } catch (error) {
      console.error('Error fetching district admin districts:', error)
    }
  }

  const handleDistrictAdminSearch = (value) => {
    setDistrictAdminSearchTerm(value)
    fetchDistrictAdmins(value, selectedDistrictAdminDistrict)
  }

  const handleDistrictAdminDistrictFilter = (district) => {
    setSelectedDistrictAdminDistrict(district)
    fetchDistrictAdmins(districtAdminSearchTerm, district, 1)
  }

  const handleDistrictAdminPageChange = (newPage) => {
    fetchDistrictAdmins(districtAdminSearchTerm, selectedDistrictAdminDistrict, newPage)
  }

  const clearDistrictAdminFilters = () => {
    setDistrictAdminSearchTerm('')
    setSelectedDistrictAdminDistrict('')
    fetchDistrictAdmins('', '')
  }

  const fetchDistricts = async () => {
    try {
      const response = await api.get('/ihthisabi/admin/unitadmins/districts')
      setDistricts(response.data.data.districts || [])
    } catch (error) {
      console.error('Error fetching districts:', error)
    }
  }

  const fetchDistrictStats = async () => {
    try {
      setDistrictStatsLoading(true)
      const response = await api.get('/ihthisabi/admin/dashboard/district-stats')
      setDistrictStats(response.data.data.districts || [])
    } catch (error) {
      console.error('Error fetching district stats:', error)
    } finally {
      setDistrictStatsLoading(false)
    }
  }

  const fetchUnitsStatus = async (district = '', area = '') => {
    try {
      setUnitsStatusLoading(true)
      const params = {}
      if (district) params.district = district
      if (area) params.area = area
      const response = await api.get('/ihthisabi/admin/dashboard/units-status', { params })
      setUnitsStatus(response.data.data)
    } catch (error) {
      console.error('Error fetching units status:', error)
      toast.error('Failed to load unit submission status')
    } finally {
      setUnitsStatusLoading(false)
    }
  }

  const handleUnitsStatusDistrictChange = (district) => {
    setUnitsStatusDistrict(district)
    setUnitsStatusArea('')
    fetchUnitsStatus(district, '')
  }

  const handleUnitsStatusAreaChange = (area) => {
    setUnitsStatusArea(area)
    fetchUnitsStatus(unitsStatusDistrict, area)
  }

  const handleUnitAdminExcelUpload = async (file) => {
    try {
      setUploading(true)
      setUploadProgress(0)
      setUploadResult(null)
      
      const formData = new FormData()
      formData.append('excelFile', file)
      
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval)
            return 90
          }
          return prev + 10
        })
      }, 200)
      
      const response = await api.post('/ihthisabi/admin/upload-unitadmin-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      clearInterval(progressInterval)
      setUploadProgress(100)
      
      if (response.data.success) {
        setUploadResult({
          success: true,
          totalProcessed: response.data.data.totalProcessed,
          created: response.data.data.created,
          updated: response.data.data.updated,
          errors: response.data.data.errors || []
        })
        
        toast.success(`Unit Admin Excel processed: ${response.data.data.created} created, ${response.data.data.updated} updated`)
        fetchUnitAdmins(searchTerm, selectedDistrict) // Refresh the list with current filters
        fetchDistricts() // Refresh districts list
        
        // Clear result after 5 seconds
        setTimeout(() => {
          setUploadResult(null)
        }, 5000)
      }
    } catch (error) {
      console.error('Error uploading unit admin Excel:', error)
      setUploadResult({
        success: false,
        error: error.response?.data?.message || 'Failed to upload unit admin Excel file'
      })
      toast.error('Failed to upload unit admin Excel file')
    } finally {
      setUploading(false)
      setTimeout(() => {
        setUploadProgress(0)
      }, 1000)
    }
  }

  const handleDeleteAllUnitAdmins = async () => {
    try {
      const response = await api.delete('/ihthisabi/admin/unitadmins/delete-all')
      
      if (response.data.success) {
        toast.success(`Successfully deleted ${response.data.data.deletedCount} unit admins`)
        setUnitAdmins([]) // Clear the list
        setShowDeleteConfirm(false)
      }
    } catch (error) {
      console.error('Error deleting all unit admins:', error)
      toast.error(`Failed to delete all unit admins: ${error.response?.data?.message || error.message}`)
    }
  }

  const handleSendUnitAdminMessage = async () => {
    if (!isSuperAdmin) {
      toast.error('Only super admin can send broadcast messages')
      return
    }

    if (!unitAdminMessage.title.trim() || !unitAdminMessage.description.trim()) {
      toast.error('Please provide both a title and description')
      return
    }

    try {
      setUnitAdminMessageSending(true)
      const response = await api.post('/ihthisabi/admin/unitadmins/whatsapp-broadcast', {
        title: unitAdminMessage.title,
        description: unitAdminMessage.description
      })

      if (response.data.success) {
        const { sentCount, failedCount, missingContactCount, totalRecipients } = response.data.data.broadcast
        toast.success(`Message sent to ${sentCount} of ${totalRecipients} unit admins`)

        if (failedCount > 0 || missingContactCount > 0) {
          toast(`Failed: ${failedCount}, Missing contact: ${missingContactCount}`, {
            duration: 5000,
            icon: '⚠️'
          })
        }

        setUnitAdminMessage({ title: '', description: '' })
      }
    } catch (error) {
      console.error('Error sending unit admin message:', error)
      toast.error(error.response?.data?.message || 'Failed to send message')
    } finally {
      setUnitAdminMessageSending(false)
    }
  }

  const handleSearch = (value) => {
    setSearchTerm(value)
    fetchUnitAdmins(value, selectedDistrict)
  }

  const handleDistrictFilter = (district) => {
    setSelectedDistrict(district)
    fetchUnitAdmins(searchTerm, district, 1) // Reset to page 1 when filtering
  }

  const handlePageChange = (newPage) => {
    fetchUnitAdmins(searchTerm, selectedDistrict, newPage)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedDistrict('')
    fetchUnitAdmins('', '')
  }

  const _getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-50 border-green-200'
      case 'reviewed': return 'text-amber-600 bg-amber-50 border-amber-200'
      case 'submitted': return 'text-blue-600 bg-blue-50 border-blue-200'
      default: return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return 'N/A'
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  // Show loading while authentication is being checked
  if (authLoading || loading) {
    return (
      <div className="ih-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">
            {authLoading ? 'Authenticating...' : 'Loading dashboard...'}
          </p>
        </div>
      </div>
    )
  }

  // Show error if not authenticated or not admin
  if (!isAuthenticated || !user || user.role !== 'admin') {
    return (
      <div className="ih-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="ih-screen bg-gray-50">
      <div className="ih-page-shell">
        {/* Header — hidden below lg: the app bar (visible <lg) already names the
            page and shows the user's identity; showing this too would duplicate both. */}
        <div className="ih-page-header hidden lg:flex">
          <div className="min-w-0">
            <h1 className="ih-page-title">Admin Dashboard</h1>
            <p className="ih-page-subtitle truncate">
              Welcome back, <span className="font-medium text-gray-900">{user?.name || user?.email}</span>
            </p>
          </div>
          {hasError && (
            <button
              onClick={retryFailedRequests}
              className="btn-primary shrink-0 gap-1.5 px-2.5 py-1.5 text-[11px] sm:px-4 sm:py-2 sm:text-sm"
            >
              <Activity className="w-3.5 h-3.5" />
              Retry
            </button>
          )}
        </div>

        {/* The header above is desktop-only, so failed loads need their own
            retry surface below lg. */}
        {hasError && (
          <div className="lg:hidden mb-3 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5">
            <p className="min-w-0 text-xs font-medium text-red-700">Some data failed to load.</p>
            <button
              onClick={retryFailedRequests}
              className="btn-primary shrink-0 gap-1.5 px-3.5 text-xs"
            >
              <Activity className="w-3.5 h-3.5" />
              Retry
            </button>
          </div>
        )}

        {/* Content based on active tab */}
        {activeTab === 'archive' ? (
          <ArchiveManagement />
        ) : activeTab === 'abroad-countries' ? (
          <AbroadCountryManagement />
        ) : activeTab === 'abroad-members' ? (
          <AbroadMemberManagement />
        ) : activeTab === 'master-data' ? (
          <MasterDataManagement />
        ) : activeTab === 'user-management' ? (
          <UserManagementDynamic />
        ) : activeTab === 'users' ? (
          <UserManagement />
        ) : activeTab === 'unitreply' ? (
          <UnitReplyPanel />
        ) : activeTab === 'unitadmins' ? (
          <div className="space-y-6">
            

            {/* Unit Admin Excel Upload */}
            <div className="ih-surface p-3 sm:p-6">
              {/* Tap to expand on phones; always open from lg: where there is room. */}
              <button
                type="button"
                onClick={() => setUploadCardOpen(o => !o)}
                aria-expanded={uploadCardOpen}
                className="flex w-full items-center justify-between gap-2 text-left lg:pointer-events-none"
              >
                <h3 className="text-sm font-semibold text-gray-900 sm:text-lg">Upload Unit Admin Excel</h3>
                <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-300 lg:hidden ${uploadCardOpen ? 'rotate-180' : ''}`} />
              </button>
              <p className={`${uploadCardOpen ? 'block' : 'hidden'} mt-2 text-sm text-gray-600 lg:!block lg:mt-4 lg:mb-4`}>
                Upload an Excel file containing unit admin data. Expected format: Unit, District/City, Area (optional), Rukn ID, Rukn Name, Contact No, Email Id
              </p>
              
              {/* Upload Section */}
              <div className={`${uploadCardOpen ? 'block' : 'hidden'} mt-3 space-y-4 lg:!block`}>
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      const file = e.target.files[0]
                      if (file) {
                        handleUnitAdminExcelUpload(file)
                      }
                    }}
                    disabled={uploading}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-600 disabled:opacity-50"
                  />
                </div>

                {/* Upload Progress */}
                {uploading && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Uploading...</span>
                      <span className="text-gray-600">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Upload Results */}
                {uploadResult && (
                  <div className={`p-4 rounded-lg border ${
                    uploadResult.success 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    {uploadResult.success ? (
                      <div>
                        <div className="flex items-center mb-2">
                          <svg className="h-5 w-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <h4 className="text-sm font-semibold text-green-800">Upload Completed Successfully!</h4>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">{uploadResult.totalProcessed}</div>
                            <div className="text-green-700">Total Processed</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">{uploadResult.created}</div>
                            <div className="text-blue-700">New Created</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600">{uploadResult.updated}</div>
                            <div className="text-purple-700">Updated</div>
                          </div>
                        </div>
                        {uploadResult.errors && uploadResult.errors.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm text-red-600 font-medium">Errors ({uploadResult.errors.length}):</p>
                            <div className="max-h-32 overflow-y-auto">
                              {uploadResult.errors.slice(0, 5).map((error, index) => (
                                <p key={index} className="text-xs text-red-600">
                                  {error.ruknId} - {error.name}: {error.error}
                                </p>
                              ))}
                              {uploadResult.errors.length > 5 && (
                                <p className="text-xs text-red-600">... and {uploadResult.errors.length - 5} more errors</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <svg className="h-5 w-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <div>
                          <h4 className="text-sm font-semibold text-red-800">Upload Failed</h4>
                          <p className="text-sm text-red-600">{uploadResult.error}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Unit Admin WhatsApp Broadcast */}
            <div className="ih-surface p-3 sm:p-6">
              <button
                type="button"
                onClick={() => setBroadcastCardOpen(o => !o)}
                aria-expanded={broadcastCardOpen}
                className="flex w-full items-center justify-between gap-2 text-left lg:pointer-events-none lg:mb-4"
              >
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 sm:text-lg">Send WhatsApp Message</h3>
                  <p className="hidden text-sm text-gray-600 lg:block">This message will be sent to all active unit admins.</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {!isSuperAdmin && (
                    <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded-full">
                      Super admin only
                    </span>
                  )}
                  <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-300 lg:hidden ${broadcastCardOpen ? 'rotate-180' : ''}`} />
                </div>
              </button>

              <div className={`${broadcastCardOpen ? 'grid' : 'hidden'} mt-3 grid-cols-1 gap-4 lg:!grid lg:mt-0`}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={unitAdminMessage.title}
                    onChange={(e) => setUnitAdminMessage(prev => ({ ...prev, title: e.target.value }))}
                    className="form-input text-base"
                    placeholder="Enter message title"
                    disabled={!isSuperAdmin || unitAdminMessageSending}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={unitAdminMessage.description}
                    onChange={(e) => setUnitAdminMessage(prev => ({ ...prev, description: e.target.value }))}
                    className="form-textarea min-h-[120px] text-base"
                    placeholder="Enter message description"
                    disabled={!isSuperAdmin || unitAdminMessageSending}
                  />
                </div>
              </div>

              <div className={`${broadcastCardOpen ? 'flex' : 'hidden'} mt-4 items-center space-x-3 lg:!flex`}>
                <button
                  onClick={handleSendUnitAdminMessage}
                  disabled={!isSuperAdmin || unitAdminMessageSending || !unitAdminMessage.title.trim() || !unitAdminMessage.description.trim()}
                  className="btn-primary bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {unitAdminMessageSending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Unit Admins List */}
            <div className="ih-surface">
              <div className="border-b border-gray-200 px-3 py-2.5 sm:px-6 sm:py-4">
                <div className="flex items-center justify-between gap-2 sm:gap-3">
                  <div className="min-w-0">
                    {/* Title only from lg: — the app bar already names this screen. */}
                    <h3 className="hidden text-lg font-semibold text-gray-900 lg:block">Unit Admins</h3>
                    <div className="truncate text-[11px] text-gray-600 sm:text-sm">
                      <span>Total Count: <span className="font-semibold text-blue-600">{pagination.total || unitAdmins.length}</span></span>
                      {pagination.pages > 1 && (
                        <span className="ml-3 sm:ml-4">Page: <span className="font-semibold text-green-600">{pagination.current}</span> of <span className="font-semibold text-green-600">{pagination.pages}</span></span>
                      )}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      onClick={() => setUnitAdminFiltersOpen(o => !o)}
                      title="Search and filter"
                      aria-expanded={unitAdminFiltersOpen}
                      className={`inline-flex h-[44px] shrink-0 items-center gap-1 rounded-full px-3 text-[11px] font-medium transition-colors sm:hidden ${
                        (searchTerm || selectedDistrict) ? 'bg-primary/10 text-primary' : 'text-gray-500'
                      }`}
                      style={(searchTerm || selectedDistrict) ? undefined : { backgroundColor: 'rgba(16,24,40,0.04)' }}
                    >
                      <SlidersHorizontal className="h-4 w-4" />
                      <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${unitAdminFiltersOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {unitAdmins.length > 0 && (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        title="Delete all unit admins"
                        aria-label="Delete all unit admins"
                        className="btn-primary h-[44px] w-[44px] shrink-0 gap-1 rounded-full bg-red-600 px-0 hover:bg-red-700 sm:h-9 sm:w-auto sm:px-4 sm:text-sm"
                      >
                        <XCircle className="h-4 w-4" />
                        <span className="hidden sm:inline">Delete All</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Search + district filter — collapsed on phones, always open from sm: */}
                <div className={`${unitAdminFiltersOpen ? 'grid' : 'hidden'} mt-2.5 grid-cols-1 gap-2 sm:!grid sm:grid-cols-2 lg:grid-cols-3`}>
                  <div className="relative">
                    <Search className="ih-filter-icon" />
                    <input
                      type="text"
                      placeholder="Search unit admins..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="ih-field h-[44px] pr-3 text-base sm:h-9 sm:text-sm"
                    />
                  </div>

                  <div className="relative">
                    <MapPin className="ih-filter-icon" />
                    <select
                      value={selectedDistrict}
                      onChange={(e) => handleDistrictFilter(e.target.value)}
                      className="ih-filter-select h-[44px] truncate text-[13px] sm:h-9 sm:text-sm"
                    >
                      <option value="">All Districts</option>
                      {districts.map((district) => (
                        <option key={district} value={district}>
                          {district}
                        </option>
                      ))}
                    </select>
                  </div>

                  {(searchTerm || selectedDistrict) && (
                    <button
                      onClick={clearFilters}
                      className="inline-flex h-[44px] items-center justify-center gap-1 rounded-full px-3 text-[11px] font-medium text-gray-500 transition-colors hover:text-gray-800 sm:h-9 sm:text-sm"
                      style={{ backgroundColor: 'rgba(16,24,40,0.04)' }}
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="p-3 sm:p-6">
                {unitAdminLoading ? (
                  <div className="text-center py-8">
                    <div className="spinner w-8 h-8 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading unit admins...</p>
                  </div>
                ) : unitAdmins.length > 0 ? (
                  <>
                    {/* Mobile: roomy tappable rows — one full-width target per record */}
                    <div className="lg:hidden">
                      <div className="divide-y divide-gray-100">
                        {unitAdmins.map((admin) => (
                          <button
                            key={admin._id}
                            onClick={() => {
                              setSelectedUnitAdminId(admin._id)
                              setShowProfileModal(true)
                            }}
                            className="w-full min-h-[56px] flex items-center gap-3 px-1 py-3 text-left active:bg-gray-50 transition-colors"
                          >
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                <span className="text-sm font-medium text-primary-600">
                                  {admin.name?.charAt(0)?.toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[13px] font-semibold text-gray-900 break-words leading-snug">{admin.name}</p>
                              <p className="text-[11px] text-gray-500 mt-0.5">
                                ID: {admin.ruknId} · {admin.unit} · {admin.district}{admin.area ? `, ${admin.area}` : ''}
                              </p>
                              <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                                {admin.contactNo}{admin.emailId ? ` · ${admin.emailId}` : ''}
                              </p>
                              <span className={`mt-1 inline-block px-2 py-0.5 text-[10px] font-medium rounded-full ${
                                admin.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {admin.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Desktop: table */}
                    <div className="hidden lg:block overflow-x-hidden sm:overflow-x-auto">
                    <ResponsiveTable className="ih-table-compact w-full table-fixed divide-y divide-gray-200 text-[11px] sm:min-w-full sm:table-auto sm:text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Unit Admin
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Unit
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            District
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Area
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contact
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {unitAdmins.map((admin) => (
                          <tr
                            key={admin._id}
                            onClick={() => {
                              setSelectedUnitAdminId(admin._id)
                              setShowProfileModal(true)
                            }}
                            className="cursor-pointer hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                    <span className="text-sm font-medium text-primary-600">
                                      {admin.name?.charAt(0)?.toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{admin.name}</div>
                                  <div className="text-sm text-gray-500">ID: {admin.ruknId}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {admin.unit}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {admin.district}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {admin.area || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div>
                                <div className="flex items-center">
                                  <Phone className="h-4 w-4 mr-1" />
                                  {admin.contactNo}
                                </div>
                                <div className="flex items-center mt-1">
                                  <Mail className="h-4 w-4 mr-1" />
                                  {admin.emailId}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                admin.isActive
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {admin.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </ResponsiveTable>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No unit admins found</p>
                    <p className="text-sm text-gray-400 mt-1">Upload an Excel file to add unit admins</p>
                  </div>
                )}

                <Pagination pagination={pagination} onPageChange={handlePageChange} loading={unitAdminLoading} itemLabel="unit admins" />
              </div>
            </div>
          </div>
        ) : activeTab === 'districtadmins' ? (
          <div className="space-y-6">
            {/* Phones get the add action as a floating button instead of a full row. */}
            <button
              onClick={() => { setSelectedDistrictAdminId(null); setShowDistrictAdminModal(true) }}
              title="Add District Admin"
              aria-label="Add District Admin"
              className="ih-fab"
            >
              <Plus className="h-5 w-5" />
            </button>
            {/* District Admins List */}
            <div className="ih-surface">
              <div className="border-b border-gray-200 px-3 py-2.5 sm:px-6 sm:py-4">
                <div className="flex items-center justify-between gap-2 sm:gap-3">
                  <div className="min-w-0">
                    {/* Title only from lg: — the app bar already names this screen. */}
                    <h3 className="hidden text-lg font-semibold text-gray-900 lg:block">District Admins</h3>
                    <div className="truncate text-[11px] text-gray-600 sm:text-sm">
                      <span>Total Count: <span className="font-semibold text-blue-600">{districtAdminPagination.total || districtAdmins.length}</span></span>
                      {districtAdminPagination.pages > 1 && (
                        <span className="ml-3 sm:ml-4">Page: <span className="font-semibold text-green-600">{districtAdminPagination.current}</span> of <span className="font-semibold text-green-600">{districtAdminPagination.pages}</span></span>
                      )}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      onClick={() => setDistrictAdminFiltersOpen(o => !o)}
                      title="Search and filter"
                      aria-expanded={districtAdminFiltersOpen}
                      className={`inline-flex h-[44px] shrink-0 items-center gap-1 rounded-full px-3 text-[11px] font-medium transition-colors sm:hidden ${
                        (districtAdminSearchTerm || selectedDistrictAdminDistrict) ? 'bg-primary/10 text-primary' : 'text-gray-500'
                      }`}
                      style={(districtAdminSearchTerm || selectedDistrictAdminDistrict) ? undefined : { backgroundColor: 'rgba(16,24,40,0.04)' }}
                    >
                      <SlidersHorizontal className="h-4 w-4" />
                      <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${districtAdminFiltersOpen ? 'rotate-180' : ''}`} />
                    </button>

                    <button
                      onClick={() => { setSelectedDistrictAdminId(null); setShowDistrictAdminModal(true) }}
                      className="btn-primary hidden shrink-0 gap-1 sm:inline-flex sm:h-9 sm:px-4 sm:text-sm"
                    >
                      <Plus className="h-4 w-4" /> Add District Admin
                    </button>
                  </div>
                </div>

                {/* Search + district filter — collapsed on phones, always open from sm: */}
                <div className={`${districtAdminFiltersOpen ? 'grid' : 'hidden'} mt-2.5 grid-cols-1 gap-2 sm:!grid sm:grid-cols-2 lg:grid-cols-3`}>
                  <div className="relative">
                    <Search className="ih-filter-icon" />
                    <input
                      type="text"
                      placeholder="Search district admins..."
                      value={districtAdminSearchTerm}
                      onChange={(e) => handleDistrictAdminSearch(e.target.value)}
                      className="ih-field h-[44px] pr-3 text-base sm:h-9 sm:text-sm"
                    />
                  </div>

                  <div className="relative">
                    <MapPin className="ih-filter-icon" />
                    <select
                      value={selectedDistrictAdminDistrict}
                      onChange={(e) => handleDistrictAdminDistrictFilter(e.target.value)}
                      className="ih-filter-select h-[44px] truncate text-[13px] sm:h-9 sm:text-sm"
                    >
                      <option value="">All Districts</option>
                      {districtAdminDistricts.map((district) => (
                        <option key={district} value={district}>{district}</option>
                      ))}
                    </select>
                  </div>

                  {(districtAdminSearchTerm || selectedDistrictAdminDistrict) && (
                    <button
                      onClick={clearDistrictAdminFilters}
                      className="inline-flex h-[44px] items-center justify-center gap-1 rounded-full px-3 text-[11px] font-medium text-gray-500 transition-colors hover:text-gray-800 sm:h-9 sm:text-sm"
                      style={{ backgroundColor: 'rgba(16,24,40,0.04)' }}
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="p-3 sm:p-6">
                {districtAdminLoading ? (
                  <div className="text-center py-8">
                    <div className="spinner w-8 h-8 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading district admins...</p>
                  </div>
                ) : districtAdmins.length > 0 ? (
                  <>
                    {/* Mobile: roomy tappable rows — one full-width target per record */}
                    <div className="lg:hidden">
                      <div className="divide-y divide-gray-100">
                        {districtAdmins.map((admin) => (
                          <button
                            key={admin._id}
                            onClick={() => { setSelectedDistrictAdminId(admin._id); setShowDistrictAdminModal(true) }}
                            className="w-full min-h-[56px] flex items-center gap-3 px-1 py-3 text-left active:bg-gray-50 transition-colors"
                          >
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                <span className="text-sm font-medium text-primary-600">
                                  {admin.name?.charAt(0)?.toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[13px] font-semibold text-gray-900 break-words leading-snug">{admin.name}</p>
                              <p className="text-[11px] text-gray-500 mt-0.5">
                                ID: {admin.ruknId} · {admin.district}
                              </p>
                              <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                                {admin.contactNo || '-'}{admin.emailId ? ` · ${admin.emailId}` : ''}
                              </p>
                              <span className={`mt-1 inline-block px-2 py-0.5 text-[10px] font-medium rounded-full ${
                                admin.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {admin.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Desktop: table */}
                    <div className="hidden lg:block overflow-x-hidden sm:overflow-x-auto">
                    <ResponsiveTable className="ih-table-compact w-full table-fixed divide-y divide-gray-200 text-[11px] sm:min-w-full sm:table-auto sm:text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">District Admin</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">District</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {districtAdmins.map((admin) => (
                          <tr
                            key={admin._id}
                            onClick={() => { setSelectedDistrictAdminId(admin._id); setShowDistrictAdminModal(true) }}
                            className="cursor-pointer hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                    <span className="text-sm font-medium text-primary-600">
                                      {admin.name?.charAt(0)?.toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{admin.name}</div>
                                  <div className="text-sm text-gray-500">ID: {admin.ruknId}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{admin.district}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div>
                                <div className="flex items-center">
                                  <Phone className="h-4 w-4 mr-1" />
                                  {admin.contactNo || '-'}
                                </div>
                                <div className="flex items-center mt-1">
                                  <Mail className="h-4 w-4 mr-1" />
                                  {admin.emailId || '-'}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                admin.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {admin.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </ResponsiveTable>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No district admins found</p>
                    <p className="text-sm text-gray-400 mt-1">Run the district admin migration script, or add one manually</p>
                  </div>
                )}

                <Pagination pagination={districtAdminPagination} onPageChange={handleDistrictAdminPageChange} loading={districtAdminLoading} itemLabel="district admins" />
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            {dashboardData && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-3 sm:mb-6">
            {/* Total Users */}
            <div
              onClick={() => navigate('members')}
              className="ih-stat-card cursor-pointer hover:shadow-md active:scale-[0.99] transition"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="ih-stat-label">Total Users</p>
                  <p className="ih-stat-value mt-1">{dashboardData.totalRuknUsers || 0}</p>
                </div>
                <div className="ih-stat-icon bg-blue-50">
                  <Users className="w-4 h-4 text-blue-600" />
                </div>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] sm:text-xs">
                <span className="font-medium text-blue-600">{dashboardData.maleUsers || 0} M</span>
                <span className="font-medium text-pink-600">{dashboardData.femaleUsers || 0} F</span>
              </div>
            </div>

            {/* Quarter Comparison */}
            <div className="ih-stat-card">
              <div className="flex items-start justify-between gap-2">
                <p className="ih-stat-label">Quarter Comparison</p>
                <div className="ih-stat-icon bg-green-50">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                </div>
              </div>
              <div className="mt-1 flex items-baseline gap-1.5">
                <span className="text-base font-bold leading-none text-gray-400 sm:text-xl">
                  {dashboardData.previousQuarterSubmissions || 0}
                </span>
                <span className="text-gray-300">→</span>
                <span className="ih-stat-value">{dashboardData.currentQuarterSubmissions || 0}</span>
              </div>
              <p className="ih-stat-meta mt-1">
                Q{dashboardData.prevQuarter} {dashboardData.prevYear} → Q{dashboardData.currentQuarter} {dashboardData.currentYear}
              </p>
              <p className="ih-stat-meta mt-0.5">
                {dashboardData.previousQuarterCompletionPercent ?? 0}% → <span className="font-semibold text-gray-700">{dashboardData.currentQuarterCompletionPercent ?? 0}%</span> of members
              </p>
            </div>

            {/* Current Period Submissions */}
            <div
              onClick={() => navigate('submissions')}
              className="ih-stat-card cursor-pointer hover:shadow-md active:scale-[0.99] transition"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="ih-stat-label">This Period</p>
                  <p className="ih-stat-value mt-1">{dashboardData.currentQuarterSubmissions || 0}</p>
                </div>
                <div className="ih-stat-icon bg-purple-50">
                  <FileText className="w-4 h-4 text-purple-600" />
                </div>
              </div>
              {/* Male + Female (+ Other) always adds up to the total above */}
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] sm:text-xs">
                <span className="font-medium text-blue-600">{dashboardData.currentQuarterMale || 0} M</span>
                <span className="font-medium text-pink-600">{dashboardData.currentQuarterFemale || 0} F</span>
                {dashboardData.currentQuarterOther > 0 && (
                  <span
                    className="font-medium text-gray-500"
                    title="Submissions from members who are now inactive, removed, or have no gender on record"
                  >
                    {dashboardData.currentQuarterOther} other
                  </span>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); navigate('submissions') }}
                  className="font-medium text-blue-600 transition-colors hover:text-blue-700"
                >
                  View all →
                </button>
              </div>
              {/* Completion against active members */}
              <div className="mt-2">
                <div
                  className="h-1.5 w-full overflow-hidden rounded-full bg-purple-100"
                  role="progressbar"
                  aria-label="Members who submitted this period"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={dashboardData.currentQuarterCompletionPercent ?? 0}
                >
                  <div
                    className="h-full rounded-full bg-purple-500"
                    style={{ width: `${dashboardData.currentQuarterCompletionPercent ?? 0}%` }}
                  />
                </div>
                <p className="ih-stat-meta mt-1">
                  <span className="font-semibold text-gray-700">{dashboardData.currentQuarterCompletionPercent ?? 0}%</span>
                  {' '}· {dashboardData.currentQuarterSubmittedMembers ?? 0}/{dashboardData.totalRuknUsers || 0} members
                </p>
              </div>
            </div>

            {/* Quarter Change */}
            {(() => {
              const pct = dashboardData.quarterChangePercent ?? 0;
              const isUp = pct >= 0;
              return (
                <div className="ih-stat-card">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="ih-stat-label">vs Previous Quarter</p>
                      <p className={`ih-stat-value mt-1 ${isUp ? 'text-emerald-600' : 'text-red-500'}`}>
                        {isUp ? '+' : ''}{pct}%
                      </p>
                    </div>
                    <div className={`ih-stat-icon ${isUp ? 'bg-emerald-50' : 'bg-red-50'}`}>
                      <ArrowUpRight className={`w-4 h-4 ${isUp ? 'text-emerald-600' : 'rotate-90 text-red-500'}`} />
                    </div>
                  </div>
                  <div className="ih-stat-meta mt-1.5 flex items-center gap-1">
                    <Activity className="w-3 h-3 shrink-0" />
                    Q{dashboardData.prevQuarter} → Q{dashboardData.currentQuarter}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Charts */}
        {dashboardData && (
          <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4 mb-3 sm:mb-6">
            {/* Current quarter gender submissions chart */}
            <div className="ih-section-card p-2.5 sm:p-5">
              <h3 className="mb-2 text-[11px] font-semibold text-gray-700 sm:text-sm">
                Q{dashboardData.currentQuarter} {dashboardData.currentYear} — Submissions by Gender
              </h3>
              <ResponsiveContainer width="100%" height={170}>
                <BarChart
                  data={[
                    { name: 'Male', value: dashboardData.currentQuarterMale || 0, members: dashboardData.maleUsers || 0, fill: '#3b82f6' },
                    { name: 'Female', value: dashboardData.currentQuarterFemale || 0, members: dashboardData.femaleUsers || 0, fill: '#ec4899' },
                  ]}
                  margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                >
                  <CartesianGrid {...GRID} />
                  <XAxis dataKey="name" {...AXIS} />
                  <YAxis {...AXIS} allowDecimals={false} width={32} />
                  <Tooltip
                    {...TOOLTIP}
                    formatter={(value, _name, item) => [
                      `${value} of ${item.payload.members} (${pctOf(value, item.payload.members)}%)`,
                      'Submitted',
                    ]}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {[{ fill: '#3b82f6' }, { fill: '#ec4899' }].map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Members Status pie */}
            <div className="ih-section-card p-2.5 sm:p-5">
              <h3 className="mb-2 text-[11px] font-semibold text-gray-700 sm:text-sm">Members Status</h3>
              {(() => {
                const totalUsers = dashboardData.totalRuknUsers || 0;
                const submitted = dashboardData.currentQuarterSubmittedMembers || 0;
                const notSubmitted = dashboardData.currentQuarterPendingMembers || 0;
                // Counts live in the legend rather than in slice labels — outside
                // labels overflow the chart box on narrow screens.
                const pieData = [
                  { name: `Submitted ${submitted} (${pctOf(submitted, totalUsers)}%)`, value: submitted },
                  { name: `Pending ${notSubmitted} (${pctOf(notSubmitted, totalUsers)}%)`, value: notSubmitted },
                ];
                return (
                  <ResponsiveContainer width="100%" height={170}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" outerRadius={55} dataKey="value" labelLine={false}>
                        <Cell fill="#10b981" />
                        <Cell fill="#e5e7eb" />
                      </Pie>
                      <Tooltip {...TOOLTIP} cursor={false} />
                      {/* Legend text in grey: the pale Pending slice colour is unreadable as text */}
                      <Legend
                        iconSize={8}
                        iconType="circle"
                        wrapperStyle={{ fontSize: 11 }}
                        formatter={(value) => <span className="text-gray-600">{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                );
              })()}
            </div>
          </div>

          {/* Quarter comparison gauge */}
          {(() => {
            const prev = dashboardData.previousQuarterSubmissions || 0;
            const curr = dashboardData.currentQuarterSubmissions || 0;
            const gaugeData = [
              { name: `Q${dashboardData.prevQuarter} ${dashboardData.prevYear}`, value: dashboardData.previousQuarterCompletionPercent ?? 0, fill: '#94a3b8', rawCount: prev },
              { name: `Q${dashboardData.currentQuarter} ${dashboardData.currentYear}`, value: dashboardData.currentQuarterCompletionPercent ?? 0, fill: '#002349', rawCount: curr },
            ];
            return (
              <div className="ih-surface p-3 sm:p-6 mb-8">
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Quarter vs Quarter Submissions</h3>
                <p className="text-xs text-gray-400 mb-4">
                  Share of the {dashboardData.totalRuknUsers || 0} active members who submitted in each quarter
                </p>
                <ResponsiveContainer width="100%" height={200}>
                  <RadialBarChart
                    innerRadius="30%"
                    outerRadius="90%"
                    data={gaugeData}
                    startAngle={180}
                    endAngle={0}
                  >
                    {/* Full arc = 100% of members */}
                    <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                    <RadialBar
                      minAngle={5}
                      dataKey="value"
                      cornerRadius={6}
                      label={false}
                    />
                    <Tooltip
                      formatter={(value, name, props) => [`${props.payload.rawCount} submissions · ${value}% of members`, props.payload.name]}
                    />
                    <Legend
                      iconType="circle"
                      formatter={(value, entry) => `${entry.payload.name}: ${entry.payload.rawCount} (${entry.payload.value}%)`}
                    />
                  </RadialBarChart>
                </ResponsiveContainer>
              </div>
            );
          })()}
          </>
        )}

        {/* District-wise completion: active members vs members who submitted */}
        {dashboardData && (
          <div className="ih-surface p-3 sm:p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-700">District-wise Submissions</h3>
                <p className="text-xs text-gray-400">
                  {districtStats.length > 0 &&
                    `Members vs submitted, Q${dashboardData.currentQuarter} ${dashboardData.currentYear}, by district`}
                </p>
              </div>
              <div className="relative" ref={chartDistrictDropdownRef}>
                <button
                  type="button"
                  onClick={() => setChartDistrictDropdownOpen((v) => !v)}
                  className="form-select text-sm min-w-[220px] flex items-center justify-between gap-2 text-left"
                >
                  <span className="truncate">
                    {selectedChartDistricts.length === 0
                      ? 'Select districts...'
                      : selectedChartDistricts.length === 1
                        ? selectedChartDistricts[0]
                        : `${selectedChartDistricts.length} districts selected`}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </button>

                {chartDistrictDropdownOpen && (
                  <div className="absolute right-0 z-20 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg">
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          value={chartDistrictSearch}
                          onChange={(e) => setChartDistrictSearch(e.target.value)}
                          placeholder="Search districts..."
                          className="form-input text-base pl-8 w-full"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100">
                      <button
                        type="button"
                        onClick={() => setSelectedChartDistricts(districtStats.map((d) => d.district))}
                        className="text-xs text-blue-600 hover:text-blue-700 py-2 px-1 -mx-1"
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedChartDistricts([])}
                        className="text-xs text-gray-500 hover:text-gray-700 py-2 px-1 -mx-1"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="max-h-56 overflow-y-auto py-1">
                      {districtStats
                        .filter((d) => d.district.toLowerCase().includes(chartDistrictSearch.toLowerCase()))
                        .map((d) => (
                          <label
                            key={d.district}
                            className="flex min-h-[44px] items-center gap-2 px-3 py-2.5 text-sm hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedChartDistricts.includes(d.district)}
                              onChange={() => toggleChartDistrict(d.district)}
                              className="rounded border-gray-300"
                            />
                            <span className="flex-1 truncate">{d.district}</span>
                            <span className="text-xs text-gray-400">{d.completionPercent}%</span>
                          </label>
                        ))}
                      {districtStats.filter((d) => d.district.toLowerCase().includes(chartDistrictSearch.toLowerCase())).length === 0 && (
                        <p className="text-xs text-gray-400 text-center py-3">No matching districts</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {districtStatsLoading ? (
              <div className="text-center py-8 text-sm text-gray-500">Loading district stats...</div>
            ) : districtStats.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No district data available</p>
            ) : (
              (() => {
                const chartData = districtStats.filter((d) => selectedChartDistricts.includes(d.district))
                if (chartData.length === 0) {
                  return <p className="text-sm text-gray-400 text-center py-6">Select at least one district to display the chart</p>
                }
                return (
                  <ResponsiveContainer width="100%" height={Math.max(260, chartData.length * 44)}>
                    <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid {...GRID} />
                      <XAxis type="number" allowDecimals={false} {...AXIS} />
                      <YAxis type="category" dataKey="district" width={110} {...AXIS} />
                      <Tooltip
                        cursor={TOOLTIP.cursor}
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null
                          const d = payload[0].payload
                          return (
                            <div style={TOOLTIP.contentStyle} className="bg-white px-3 py-2">
                              <p className="mb-1 font-semibold text-gray-900">{d.district}</p>
                              <p className="text-gray-500">Total members: <span className="font-medium text-gray-900">{d.totalMembers}</span></p>
                              <p className="text-gray-500">Submitted: <span className="font-medium text-gray-900">{d.submitted}</span></p>
                              <p className="text-gray-500">Pending: <span className="font-medium text-gray-900">{d.pending}</span></p>
                              <p className="mt-1 font-semibold text-emerald-600">{d.completionPercent}% completed</p>
                            </div>
                          )
                        }}
                      />
                      <Legend />
                      <Bar dataKey="totalMembers" name="Total members" fill="#94a3b8" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="submitted" name={`Submitted (Q${dashboardData.currentQuarter} ${dashboardData.currentYear})`} fill="#002349" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )
              })()
            )}
          </div>
        )}

        {/* Unit completion: how many members of each unit submitted this period */}
        <div className="ih-surface p-3 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Unit Submission Status</h3>
              <p className="text-xs text-gray-400">
                {unitsStatus && `Q${unitsStatus.currentQuarter} ${unitsStatus.currentYear} · `}
                How many members of each unit have submitted
              </p>
            </div>
            <div className="flex gap-2">
              <select
                value={unitsStatusDistrict}
                onChange={(e) => handleUnitsStatusDistrictChange(e.target.value)}
                className="form-select text-base flex-1 min-w-0"
              >
                <option value="">All Districts</option>
                {(unitsStatus?.districtOptions || []).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <select
                value={unitsStatusArea}
                onChange={(e) => handleUnitsStatusAreaChange(e.target.value)}
                className="form-select text-base flex-1 min-w-0"
                disabled={!unitsStatusDistrict}
              >
                <option value="">All Areas</option>
                {(unitsStatus?.areaOptions || []).map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
          </div>

          {unitsStatusLoading ? (
            <div className="text-center py-8 text-sm text-gray-500">Loading unit status...</div>
          ) : unitsStatus ? (
            (() => {
              const tabs = [
                { key: 'notSubmitted', label: 'Not submitted', count: unitsStatus.notSubmittedCount, units: unitsStatus.notSubmittedUnits, activeClass: 'text-rose-600 border-b-2 border-rose-600' },
                { key: 'partial', label: 'Partial', count: unitsStatus.partialCount, units: unitsStatus.partialUnits, activeClass: 'text-amber-600 border-b-2 border-amber-600' },
                { key: 'completed', label: 'Completed', count: unitsStatus.completedCount, units: unitsStatus.completedUnits, activeClass: 'text-green-600 border-b-2 border-green-600' },
              ]
              const activeList = (tabs.find((t) => t.key === unitsListTab) || tabs[0]).units
              return (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-3">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{unitsStatus.totalUnits}</p>
                      <p className="text-xs text-gray-500">Total Units</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{unitsStatus.completedCount}</p>
                      <p className="text-xs text-gray-500">Completed</p>
                      <p className="text-[10px] text-gray-400">All members submitted</p>
                    </div>
                    <div className="text-center p-3 bg-amber-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-600">{unitsStatus.partialCount}</p>
                      <p className="text-xs text-gray-500">Partial</p>
                      <p className="text-[10px] text-gray-400">Some members submitted</p>
                    </div>
                    <div className="text-center p-3 bg-rose-50 rounded-lg">
                      <p className="text-2xl font-bold text-rose-600">{unitsStatus.notSubmittedCount}</p>
                      <p className="text-xs text-gray-500">Not submitted</p>
                      <p className="text-[10px] text-gray-400">No member submitted</p>
                    </div>
                  </div>
                  <p className="mb-4 text-xs text-gray-500">
                    Members: <span className="font-semibold text-gray-900">{unitsStatus.submittedMembers}</span> of {unitsStatus.totalMembers} submitted
                    {' '}(<span className="font-semibold text-gray-900">{unitsStatus.completionPercent}%</span>)
                    {' '}· <span className="font-semibold text-amber-600">{unitsStatus.pendingMembers}</span> pending
                  </p>

                  {/* Three equal columns on phones (count under label) so no tab is clipped */}
                  <div className="grid grid-cols-3 sm:flex sm:gap-2 mb-3 border-b border-gray-200">
                    {tabs.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setUnitsListTab(t.key)}
                        className={`min-h-[44px] px-2 sm:px-3 py-2 text-sm font-medium leading-tight ${unitsListTab === t.key ? t.activeClass : 'text-gray-500'}`}
                      >
                        <span className="block sm:inline">{t.label}</span> ({t.count})
                      </button>
                    ))}
                  </div>

                  <div className="max-h-64 overflow-y-auto">
                    {activeList.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-6">No units in this list</p>
                    ) : (
                      <>
                        {/* Mobile: roomy rows — one full-width row per record */}
                        <div className="lg:hidden divide-y divide-gray-100">
                          {activeList.map((u) => (
                            <div key={`${u.district}-${u.area}-${u.unit}`} className="min-h-[56px] flex items-center gap-3 px-1 py-3">
                              <div className="min-w-0 flex-1">
                                <p className="text-[13px] font-semibold text-gray-900 break-words leading-snug">{u.unit}</p>
                                <p className="text-[11px] text-gray-500 mt-0.5">{u.area} · {u.district}</p>
                              </div>
                              <div className="shrink-0 text-right">
                                <p className="text-[13px] font-semibold text-gray-900">{u.submitted}/{u.totalMembers}</p>
                                <p className="text-[11px] text-gray-500">{u.completionPercent}%</p>
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* Desktop: table */}
                        <div className="hidden lg:block overflow-x-auto">
                          <ResponsiveTable className="min-w-full text-sm">
                            <thead>
                              <tr className="text-left text-xs text-gray-500 uppercase">
                                <th className="py-2 pr-4">Unit</th>
                                <th className="py-2 pr-4">Area</th>
                                <th className="py-2 pr-4">District</th>
                                <th className="py-2 pr-4 text-right">Submitted / Members</th>
                                <th className="py-2 pr-4 text-right">Pending</th>
                                <th className="py-2 pr-4 text-right">Completion</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {activeList.map((u) => (
                                <tr key={`${u.district}-${u.area}-${u.unit}`}>
                                  <td className="py-2 pr-4 font-medium text-gray-900">{u.unit}</td>
                                  <td className="py-2 pr-4 text-gray-600">{u.area}</td>
                                  <td className="py-2 pr-4 text-gray-600">{u.district}</td>
                                  <td className="py-2 pr-4 text-right text-gray-900">{u.submitted} / {u.totalMembers}</td>
                                  <td className="py-2 pr-4 text-right text-gray-600">{u.pending}</td>
                                  <td className="py-2 pr-4 text-right font-medium text-gray-900">{u.completionPercent}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </ResponsiveTable>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )
            })()
          ) : (
            <p className="text-sm text-gray-400 text-center py-6">No data</p>
          )}
        </div>

        {/* Recent Submissions */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Recent Submissions</h2>
                <p className="text-sm text-gray-500 mt-0.5">Latest activity from members</p>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-xs text-gray-500">Live</span>
              </div>
            </div>
          </div>
          
          <div className="divide-y divide-gray-100">
            {submissions.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 text-gray-400" />
                </div>
                <h3 className="text-sm font-medium text-gray-900 mb-1">No submissions yet</h3>
                <p className="text-xs text-gray-500">Submissions will appear here once members start reporting</p>
              </div>
            ) : (
              submissions.slice(0, 8).map((submission) => (
                <div key={submission.id} className="px-6 py-4 hover:bg-gray-50 transition-colors duration-150">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-white">
                            {(submission.ruknName || 'U').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {submission.ruknName || 'Unknown User'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {submission.unit || 'No location'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-right">
                      <div className="hidden sm:block">
                        <p className="text-xs text-gray-500">Period</p>
                        <p className="text-sm font-medium text-gray-900">
                          {submission.periodDisplay || 'N/A'}
                        </p>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${
                          submission.status === 'submitted' ? 'bg-blue-400' :
                          submission.status === 'reviewed' ? 'bg-yellow-400' :
                          submission.status === 'approved' ? 'bg-green-400' :
                          'bg-gray-400'
                        }`}></div>
                        <span className="text-xs font-medium text-gray-600 capitalize">
                          {submission.status}
                        </span>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Submitted</p>
                        <p className="text-sm font-medium text-gray-900">
                          {formatDate(submission.submittedAt || submission.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {submissions.length > 8 && (
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
              <button 
                onClick={() => {
                  navigate('submissions')
                }}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors duration-150 py-2.5 -my-2.5 block w-full text-left"
              >
                View all submissions →
              </button>
            </div>
          )}
        </div>
        </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteAllUnitAdmins}
        title="Delete All Unit Admins"
        message={`Are you sure you want to delete all ${unitAdmins.length} unit admins? This action cannot be undone.`}
        confirmText="Delete All"
        variant="danger"
      />

      {/* Unit Admin Profile Modal */}
      <UnitAdminProfileModal
        unitAdminId={selectedUnitAdminId}
        isOpen={showProfileModal}
        onClose={() => {
          setShowProfileModal(false)
          setSelectedUnitAdminId(null)
        }}
      />

      {/* District Admin Profile Modal */}
      <DistrictAdminProfileModal
        districtAdminId={selectedDistrictAdminId}
        isOpen={showDistrictAdminModal}
        onClose={() => {
          setShowDistrictAdminModal(false)
          setSelectedDistrictAdminId(null)
        }}
        onSaved={() => fetchDistrictAdmins(districtAdminSearchTerm, selectedDistrictAdminDistrict, districtAdminPagination.current)}
        onDeleted={() => fetchDistrictAdmins(districtAdminSearchTerm, selectedDistrictAdminDistrict, 1)}
      />
    </div>
  )
}

export default AdminDashboard
